export interface TransformationType {
  value: string;
  label: string;
  description: string;
}

export const TRANSFORMATION_TYPES: TransformationType[] = [
  {
    value: 'AddPrefix',
    label: 'Add Prefix',
    description: 'Adds a prefix to resource names to match migrated resources.'
  },
  {
    value: 'DeleteInvalidData',
    label: 'Delete Invalid Data',
    description: 'Removes invalid data and cleans up JSON files.'
  },
  {
    value: 'DivisionMapping',
    label: 'Division Mapping',
    description: 'Maps division IDs from source organization to target organization.'
  },
  {
    value: 'EmailDomain',
    label: 'Email Domain',
    description: 'Updates email domains from old company domain to new company domain.'
  },
  {
    value: 'ExcludeResource',
    label: 'Exclude Resource',
    description: 'Marks resources for exclusion from migration based on criteria.'
  },
  {
    value: 'OverwriteProperty',
    label: 'Overwrite Property',
    description: 'Overwrites a specific property value across all matching resources.'
  },
  {
    value: 'PropertyBatchUpdate',
    label: 'Property Batch Update',
    description: 'Updates multiple properties in a single transformation.'
  },
  {
    value: 'RemovePrefix',
    label: 'Remove Prefix',
    description: 'Removes a specified prefix from resource names to match migrated resources.'
  },
  {
    value: 'ReplacePrefix',
    label: 'Replace Prefix',
    description: 'Swaps one prefix for another in one step (e.g. TST_ → QA_) — removes the old prefix, then adds the new one.'
  },
  {
    value: 'RenameResource',
    label: 'Rename Resource',
    description:
      'Renames resources based on provided mappings or rules (find-and-replace for resource names).'
  },
  {
    value: 'FlowLiteralReplace',
    label: 'Flow Literal Replace',
    description:
      'Find-and-replace on hand-typed text inside Architect flows (e.g. a queue name typed as a literal in an Update Data action). Renaming resources does not update these embedded values.'
  },
  {
    value: 'ResourceFilter',
    label: 'Resource Filter',
    description: 'Filters resources based on business rules to determine migration inclusion.'
  }
];

/**
 * For transformations that emit multiple sub-type labels on examples, list the
 * expected `transformationType` values here against the parent transformation name.
 * Any value not listed falls back to a direct equality check.
 */
export const TRANSFORMATION_SUBTYPE_MAP: Record<string, string[]> = {
  DeleteInvalidData: ['NullValueRemoved', 'InvalidPropertyRemoved', 'DeleteInvalidData']
};

/**
 * Checks if an example transformation type matches a transformation name,
 * accounting for sub-types defined in TRANSFORMATION_SUBTYPE_MAP.
 */
export const exampleMatchesTransformation = (
  exType: string | undefined,
  transformName: string
): boolean => {
  if (!exType) return false;
  const subtypes = TRANSFORMATION_SUBTYPE_MAP[transformName];
  if (subtypes) return subtypes.includes(exType);
  return exType === transformName;
};
